/**
 * Storico modifiche del report ENISA (log audit, E7.6): chi ha cambiato cosa e quando, anche
 * sui report precedenti già chiusi. Pannello collassabile: gli eventi sono letti solo
 * all'apertura. Visibile solo con il permesso VIEW_AUDIT_LOG (come la pagina Log Audit).
 */

import { useState } from 'react';
import { usePermissions } from '../../auth/usePermissions.js';
import { useReportAuditHistory, type SrpAuditEvent } from './auditHistory.js';
import { auditEventDetailRows, auditEventKindLabel } from '../audit/auditLabels.js';
import { REPORT_CATEGORY_LABEL, type ReportCategory } from './category.js';
import { etichettaThread } from './threadStatus.js';
import type { ThreadStatus } from './types.js';
import styles from './Srp.module.css';

const ACTION_LABEL: Record<string, string> = {
  create: 'Creazione',
  modify: 'Modifica',
  download: 'Step registrato (report VEX scaricato)',
  delete: 'Eliminazione',
};

const ENTITY_LABEL: Record<string, string> = {
  srp_report: 'Report',
  sla_timer: 'Rilevazione SLA',
};

const FIELD_LABEL: Record<string, string> = {
  analystNotes: 'Note analista',
  checklist: 'Checklist',
  category: 'Categoria',
  threadStatus: 'Stato thread',
  status: 'Stato report',
  step: 'Step',
  notification: 'Registrazione step',
  detectedAt: 'Rilevazione',
  reason: 'Motivazione',
  addedCves: 'CVE aggiunte',
  removedCves: 'CVE rimosse',
  cves: 'CVE del report',
  primaryCve: 'CVE primaria',
  subjectRef: 'Soggetto',
  cveId: 'CVE',
  productName: 'Prodotto',
  releaseVersion: 'Release',
  source: 'Origine',
  slaClock: 'Timer SLA dello step',
  undone: 'Registrazione annullata',
  ticketId: 'Ticket promosso',
  originTicketIds: 'Ticket di origine',
};

const STEP_LABEL: Record<string, string> = {
  early_warning: 'Early warning (24h)',
  vulnerability: 'Notifica 72h',
  final: 'Relazione finale',
};

/** Rende leggibile il valore di un campo (etichette di dominio, note di step, liste). */
function formatValue(field: string, value: unknown): string {
  if (value === undefined || value === null || value === '') return '—';
  if (field === 'category' && typeof value === 'string') {
    return REPORT_CATEGORY_LABEL[value as ReportCategory] ?? value;
  }
  if (field === 'threadStatus' && typeof value === 'string') {
    return etichettaThread(value as ThreadStatus);
  }
  if (field === 'step' && typeof value === 'string') return STEP_LABEL[value] ?? value;
  if (field === 'notification' && typeof value === 'object') {
    const n = value as { sentAt?: string; note?: string };
    const quando = n.sentAt ? new Date(n.sentAt).toLocaleString('it-IT') : '';
    return n.note ? `${quando} — «${n.note}»` : quando || '—';
  }
  if (field === 'slaClock' && typeof value === 'object') {
    // Timer dello step: fermato alla registrazione (in tempo / in ritardo) o ripartito (undo).
    const c = value as { deadline?: string; status?: string; stoppedAt?: string; late?: boolean };
    const data = (iso?: string) => (iso ? new Date(iso).toLocaleString('it-IT') : '—');
    if (c.status === 'stopped' && c.stoppedAt) {
      return `fermato il ${data(c.stoppedAt)} (${c.late ? 'in ritardo' : 'in tempo'}) · scadenza ${data(c.deadline)}`;
    }
    return `ripartito (${c.status ?? '—'}) · scadenza ${data(c.deadline)}`;
  }
  if (field === 'undone' && value === true) return 'sì — lo step torna da fare';
  if (Array.isArray(value)) return value.join(', ') || '—';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function asRecord(v: unknown): Record<string, unknown> {
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {};
}

/** Etichetta dell'evento (azione), con i sottotipi del modulo ENISA. */
function etichettaAzione(ev: SrpAuditEvent): string {
  return auditEventKindLabel(ev) ?? ACTION_LABEL[ev.action] ?? ev.action;
}

/** Righe campo/prima/dopo di un evento; un campo uguale prima e dopo è contesto (es. lo step). */
function righeModifica(ev: SrpAuditEvent) {
  // Campi ed export del modulo ENISA: stessa resa della pagina Log Audit.
  if (auditEventKindLabel(ev)) return auditEventDetailRows(ev);
  const before = asRecord(ev.before);
  const after = asRecord(ev.after);
  const campi = [...new Set([...Object.keys(before), ...Object.keys(after)])];
  return campi.map((campo) => {
    const prima = formatValue(campo, before[campo]);
    const dopo = formatValue(campo, after[campo]);
    return {
      campo,
      label: FIELD_LABEL[campo] ?? campo,
      prima: campo in before ? prima : null,
      dopo,
      invariato: campo in before && prima === dopo,
    };
  });
}

export function SrpAuditHistory({ reportId, timerId }: { reportId: number; timerId?: number }) {
  const { can } = usePermissions();
  const [aperto, setAperto] = useState(false);
  const consentito = can('VIEW_AUDIT_LOG');
  const {
    data: eventi,
    isLoading,
    isError,
  } = useReportAuditHistory(reportId, timerId, aperto && consentito);

  if (!consentito) return null;

  return (
    <details
      className={styles.section ?? ''}
      data-testid="srp-storico"
      onToggle={(e) => {
        setAperto((e.currentTarget as HTMLDetailsElement).open);
      }}
    >
      <summary className={styles.sectionSummary}>
        Storico modifiche{' '}
        <span className={styles.muted ?? ''}>
          (log audit{eventi ? ` — ${eventi.length} eventi` : ''})
        </span>
      </summary>
      {isLoading && <p className={styles.loadingMsg}>Caricamento storico…</p>}
      {isError && (
        <div className={styles.errorMsg} data-testid="srp-storico-errore">
          Impossibile caricare lo storico delle modifiche.
        </div>
      )}
      {eventi && eventi.length === 0 && (
        <p className={styles.muted ?? ''} data-testid="srp-storico-vuoto">
          Nessuna modifica registrata.
        </p>
      )}
      {eventi && eventi.length > 0 && (
        <ol className={styles.auditList} data-testid="srp-storico-lista">
          {eventi.map((ev) => (
            <li key={ev.eventId} data-testid={`srp-storico-${ev.eventId}`}>
              <div className={styles.auditHead}>
                <time dateTime={ev.timestamp}>
                  {new Date(ev.timestamp).toLocaleString('it-IT')}
                </time>
                <strong>
                  {etichettaAzione(ev)}
                  {ev.entityType !== 'srp_report'
                    ? ` — ${ENTITY_LABEL[ev.entityType] ?? ev.entityType}`
                    : ''}
                </strong>
                <span className={styles.muted ?? ''}>{ev.actor}</span>
              </div>
              <ul className={styles.auditChanges}>
                {righeModifica(ev).map((r) => (
                  <li key={r.campo} data-field={r.campo}>
                    <span className={styles.auditField}>{r.label}:</span>{' '}
                    {r.prima !== null && !r.invariato ? (
                      <>
                        <del>{r.prima}</del> → {r.dopo}
                      </>
                    ) : (
                      r.dopo
                    )}
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ol>
      )}
    </details>
  );
}
